import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';
import { useLocation, useSearchParams } from 'react-router-dom';
import { athleteAPI, equipmentAPI, trainingSessionAPI, bananaCupAPI } from '../../services/api';
import { Athlete } from '../../types/mongodb';
import { getQuickDateRange } from './dateRangeUtils';
import { loadAnalyticsFilters, saveAnalyticsFilters, getPagePathFromRoute } from '../../utils/analyticsFilterStorage';
import {
  AnalyticsContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  DataSelectionSection,
  SelectionGrid,
  SelectionGroup,
  GraphSection,
  GraphSectionWithPeriodSelector,
  GraphTitle,
  GraphContainer,
  EmptyState,
  PeriodSelectorTopRight,
  PeriodButtonWrapperSmall,
  PeriodButtonSmall,
  PeriodLabelSmall,
} from './styles';
import {
  DateRangeDropdown,
  AthletesDropdown,
  LocationDropdown,
  HillSizeDropdown,
  SessionTypesDropdown,
  EquipmentDropdown,
  GateDropdown,
} from './FilterDropdowns';
import { getAthleteColor } from '../../utils/athleteColors';



interface Equipment {
  _id: string;
  name: string;
  type: 'suit' | 'skis' | 'shoes';
  athleteId?: string | { _id: string; firstName: string; lastName: string };
}

interface TrainingSession {
  _id: string;
  date: string;
  location: string;
  hillSize?: string;
  trainingType: string;
  jumps?: Array<{
    athleteId: string | { _id: string; firstName: string; lastName: string };
    speed?: number;
    startingGate?: number;
    hillSize?: string;
    skisId: string;
  }>;
  competitionJumps?: Array<{
    athleteId: string | { _id: string; firstName: string; lastName: string };
    trialRoundSpeed?: number;
    trialRoundStartingGate?: number;
    trialRoundSkisId?: string;
    firstRoundSpeed?: number;
    secondRoundSpeed?: number;
    thirdRoundSpeed?: number;
    fourthRoundSpeed?: number;
    fifthRoundSpeed?: number;
    firstRoundStartingGate?: number;
    secondRoundStartingGate?: number;
    thirdRoundStartingGate?: number;
    fourthRoundStartingGate?: number;
    fifthRoundStartingGate?: number;
    firstRoundSkisId?: string;
    secondRoundSkisId?: string;
    thirdRoundSkisId?: string;
    fourthRoundSkisId?: string;
    fifthRoundSkisId?: string;
    hillSize?: string;
  }>;
  groundTrainings?: Array<any>;
  crossCountryTrainings?: Array<any>;
}

interface SpeedAnalyticsProps {
  isJumperMode?: boolean;
}

const SpeedAnalytics: React.FC<SpeedAnalyticsProps> = ({ isJumperMode = false }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pagePath = getPagePathFromRoute(location.pathname, searchParams);
  
  // Load saved filters on mount
  const savedFilters = loadAnalyticsFilters(pagePath);
  
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [bananaCups, setBananaCups] = useState<any[]>([]);
  const [selectedAthletes, setSelectedAthletes] = useState<string[]>(savedFilters?.selectedAthletes || []);
  const [selectedSkis, setSelectedSkis] = useState<string[]>(savedFilters?.selectedEquipment || []);
  const [startDate, setStartDate] = useState<string>(savedFilters?.startDate || '');
  const [endDate, setEndDate] = useState<string>(savedFilters?.endDate || '');
  const [selectedLocation, setSelectedLocation] = useState<string>(savedFilters?.selectedLocation || '');
  const [selectedHillSize, setSelectedHillSize] = useState<string>(savedFilters?.selectedHillSize || '');
  const [selectedGate, setSelectedGate] = useState<string>(savedFilters?.selectedGate || '');
  const [selectedSessionTypes, setSelectedSessionTypes] = useState<string[]>(savedFilters?.selectedSessionTypes || []);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [locations, setLocations] = useState<string[]>([]);
  const [hillSizes, setHillSizes] = useState<string[]>([]);
  const [gates, setGates] = useState<number[]>([]);
  const [jumperAthleteId, setJumperAthleteId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(typeof window !== 'undefined' && window.innerWidth <= 768);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Data states for graphs
  const [selectedPeriod, setSelectedPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [avgSpeedLineDataDaily, setAvgSpeedLineDataDaily] = useState<any[]>([]);
  const [avgSpeedLineDataWeekly, setAvgSpeedLineDataWeekly] = useState<any[]>([]);
  const [avgSpeedLineDataMonthly, setAvgSpeedLineDataMonthly] = useState<any[]>([]);
  const [topSpeedLineDataDaily, setTopSpeedLineDataDaily] = useState<any[]>([]);
  const [topSpeedLineDataWeekly, setTopSpeedLineDataWeekly] = useState<any[]>([]);
  const [topSpeedLineDataMonthly, setTopSpeedLineDataMonthly] = useState<any[]>([]);
  const [avgSpeedByGateData, setAvgSpeedByGateData] = useState<any[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // If jumper mode, get the logged-in jumper's athlete ID first
        if (isJumperMode) {
          try {
            const myAthlete = await athleteAPI.getMyAthlete();
            const athleteId = myAthlete._id.toString();
            setJumperAthleteId(athleteId);
            setSelectedAthletes([athleteId]);
          } catch (error) {
            console.error('Error loading jumper athlete:', error);
          }
        }
        
        const [athletesData, equipmentData, sessionsResponse, bananaCupsData] = await Promise.all([
          athleteAPI.getAll(),
          equipmentAPI.getAll(),
          trainingSessionAPI.getAll(undefined, undefined, true), // Load all sessions for analytics
          bananaCupAPI.getAll(),
        ]);

        // Handle response - when all=true, returns array directly, otherwise paginated object
        const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);

        setAthletes(athletesData);
        setEquipment(equipmentData);
        setSessions(sessionsData);
        setBananaCups(bananaCupsData || []);

        // Extract unique locations, hill sizes, and gates
        const uniqueLocations = Array.from(new Set(sessionsData.map((s: TrainingSession) => s.location).filter(Boolean)));
        const uniqueHillSizes = Array.from(new Set(
          sessionsData.flatMap((s: TrainingSession) => [
            s.hillSize,
            ...(s.jumps?.map(j => (j as any).hillSize) || []),
            ...(s.competitionJumps?.map(j => (j as any).hillSize) || []),
          ].filter(Boolean))
        ));
        const uniqueGates = Array.from(new Set(
          sessionsData.flatMap((s: TrainingSession) => [
            ...(s.jumps?.map(j => j.startingGate).filter(Boolean) || []),
            ...(s.competitionJumps?.flatMap(j => [
              (j as any).trialRoundStartingGate,
              j.firstRoundStartingGate,
              j.secondRoundStartingGate,
              (j as any).thirdRoundStartingGate,
              (j as any).fourthRoundStartingGate,
              (j as any).fifthRoundStartingGate,
            ].filter(Boolean)) || []),
          ])
        )).sort((a, b) => (a as number) - (b as number));

        setLocations(uniqueLocations as string[]);
        setHillSizes(uniqueHillSizes as string[]);
        setGates(uniqueGates as number[]);

        // Set default date range (last 3 months) only if no saved filters exist
        if (!savedFilters || (!savedFilters.startDate && !savedFilters.endDate)) {
          const today = new Date();
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(today.getMonth() - 3);
          setEndDate(today.toISOString().split('T')[0]);
          setStartDate(threeMonthsAgo.toISOString().split('T')[0]);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isJumperMode]);

  // Save filters whenever they change (skip on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    saveAnalyticsFilters(pagePath, {
      selectedAthletes,
      selectedEquipment: selectedSkis,
      startDate,
      endDate,
      selectedLocation,
      selectedHillSize,
      selectedSessionTypes,
      selectedGate,
      selectedPeriod,
    });
  }, [pagePath, selectedAthletes, selectedSkis, startDate, endDate, selectedLocation, selectedHillSize, selectedSessionTypes, selectedGate, selectedPeriod]);

  // Filter skis by selected athletes
  const filteredSkis = equipment.filter(eq => {
    if (eq.type !== 'skis') return false;
    
    // If no athletes selected, don't show any skis
    if (selectedAthletes.length === 0) return false;
    
    // Filter by selected athletes
    const athleteId = typeof eq.athleteId === 'object' ? eq.athleteId._id : eq.athleteId;
    return athleteId && selectedAthletes.includes(athleteId);
  });

  // Clean up selected skis when athletes change
  useEffect(() => {
    if (selectedAthletes.length === 0) {
      setSelectedSkis([]);
      return;
    }

    setSelectedSkis(prev => {
      return prev.filter(skisId => {
        const skisItem = equipment.find(eq => String(eq._id) === skisId && eq.type === 'skis');
        if (!skisItem) return false;
        
        const athleteId = typeof skisItem.athleteId === 'object' 
          ? skisItem.athleteId._id 
          : skisItem.athleteId;
        
        return athleteId && selectedAthletes.includes(athleteId);
      });
    });
  }, [selectedAthletes, equipment]);

  // Helper function to format date for input
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    if (dateString.includes('-') && dateString.length === 10) {
      return dateString;
    }
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  // Helper function to check if date is in range
  const isDateInRange = (dateString: string, start: string, end: string): boolean => {
    if (!start || !end) return true;
    return dateString >= start && dateString <= end;
  };

  // Process speed data
  useEffect(() => {
    if (!sessions.length || !startDate || !endDate || selectedSessionTypes.length === 0 || (selectedAthletes.length === 0 && selectedSkis.length === 0)) {
      setAvgSpeedLineDataDaily([]);
      setAvgSpeedLineDataWeekly([]);
      setAvgSpeedLineDataMonthly([]);
      setTopSpeedLineDataDaily([]);
      setTopSpeedLineDataWeekly([]);
      setTopSpeedLineDataMonthly([]);
      return;
    }

    // Helper function to check if a session has been recorded (has actual data)
    const isRecordedSession = (session: TrainingSession): boolean => {
      const hasJumps = !!(session.jumps && session.jumps.length > 0);
      const hasCompetitionJumps = !!(session.competitionJumps && session.competitionJumps.length > 0);
      const hasGroundTrainings = !!(session.groundTrainings && session.groundTrainings.length > 0);
      const hasCrossCountryTrainings = !!(session.crossCountryTrainings && session.crossCountryTrainings.length > 0);
      return hasJumps || hasCompetitionJumps || hasGroundTrainings || hasCrossCountryTrainings;
    };

    // Filter sessions by date range, location, and training type
    // Also exclude pre-planned sessions (sessions with no actual data)
    const filteredSessions = sessions.filter(session => {
      if (!isDateInRange(session.date, startDate, endDate)) return false;
      if (selectedLocation && session.location !== selectedLocation) return false;
      // Filter by selected session types
      if (!selectedSessionTypes.includes(session.trainingType)) return false;
      // Exclude pre-planned sessions (only include sessions with actual data)
      if (!isRecordedSession(session)) return false;
      return true;
    });

    // Get athletes to process (only selected athletes, or all if skis are selected)
    const athletesToProcess = selectedSkis.length > 0
      ? athletes // If skis are selected, we need all athletes to find their skis
      : athletes.filter(a => selectedAthletes.includes(a._id));

    // If skis are selected, group by ski instead of athlete
    const groupBySkis = selectedSkis.length > 0;

    // Collect all speeds per athlete/ski and date
    const athleteSpeedMap = new Map<string, Map<string, number[]>>(); // athleteId/skisId -> date -> speeds[]
    const skisSpeedMap = new Map<string, Map<string, number[]>>(); // skisId -> date -> speeds[]

    filteredSessions.forEach(session => {
      // Process regular jumps
      if (session.jumps) {
        session.jumps.forEach(jump => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          // Check if athlete is selected (only if skis are not selected)
          if (selectedSkis.length === 0 && !selectedAthletes.includes(athleteId)) return;
          
          // Check hill size if selected
          const jumpHillSize = jump.hillSize || session.hillSize;
          if (selectedHillSize && jumpHillSize !== selectedHillSize) return;

          // Check gate if selected
          if (selectedGate && jump.startingGate !== Number(selectedGate)) return;

          // Check skis if selected
          const skisId = typeof jump.skisId === 'object' ? (jump.skisId as any)._id : jump.skisId;
          const skisIdStr = String(skisId);
          if (selectedSkis.length > 0 && !selectedSkis.includes(skisIdStr)) return;

          if (jump.speed) {
            if (groupBySkis) {
              // Group by ski
              if (!skisSpeedMap.has(skisIdStr)) {
                skisSpeedMap.set(skisIdStr, new Map());
              }
              const dateMap = skisSpeedMap.get(skisIdStr)!;
              if (!dateMap.has(session.date)) {
                dateMap.set(session.date, []);
              }
              dateMap.get(session.date)!.push(jump.speed);
            } else {
              // Group by athlete
              if (!athleteSpeedMap.has(athleteId)) {
                athleteSpeedMap.set(athleteId, new Map());
              }
              const dateMap = athleteSpeedMap.get(athleteId)!;
              if (!dateMap.has(session.date)) {
                dateMap.set(session.date, []);
              }
              dateMap.get(session.date)!.push(jump.speed);
            }
          }
        });
      }

      // Process competition jumps (including banana_cup - can have up to 5 rounds)
      if (session.competitionJumps) {
        session.competitionJumps.forEach(jump => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          // Check if athlete is selected (only if skis are not selected)
          if (selectedSkis.length === 0 && !selectedAthletes.includes(athleteId)) return;
          
          const jumpHillSize = jump.hillSize || session.hillSize;
          if (selectedHillSize && jumpHillSize !== selectedHillSize) return;

          if (!athleteSpeedMap.has(athleteId)) {
            athleteSpeedMap.set(athleteId, new Map());
          }
          const dateMap = athleteSpeedMap.get(athleteId)!;
          if (!dateMap.has(session.date)) {
            dateMap.set(session.date, []);
          }

          // Process all rounds (trial round, competition rounds, banana cup can have up to 5 rounds)
          const rounds = [
            { speed: (jump as any).trialRoundSpeed, gate: (jump as any).trialRoundStartingGate, skisId: (jump as any).trialRoundSkisId },
            { speed: jump.firstRoundSpeed, gate: jump.firstRoundStartingGate, skisId: jump.firstRoundSkisId },
            { speed: jump.secondRoundSpeed, gate: jump.secondRoundStartingGate, skisId: jump.secondRoundSkisId },
            { speed: (jump as any).thirdRoundSpeed, gate: (jump as any).thirdRoundStartingGate, skisId: (jump as any).thirdRoundSkisId },
            { speed: (jump as any).fourthRoundSpeed, gate: (jump as any).fourthRoundStartingGate, skisId: (jump as any).fourthRoundSkisId },
            { speed: (jump as any).fifthRoundSpeed, gate: (jump as any).fifthRoundStartingGate, skisId: (jump as any).fifthRoundSkisId },
          ].filter(r => r.speed);

          rounds.forEach(round => {
            // Check gate if selected
            if (selectedGate && round.gate !== Number(selectedGate)) return;
            
            // Check skis if selected
            const skisId = typeof round.skisId === 'object' 
              ? (round.skisId as any)._id 
              : round.skisId;
            const skisIdStr = String(skisId);
            if (selectedSkis.length > 0 && !selectedSkis.includes(skisIdStr)) return;

            if (groupBySkis) {
              // Group by ski
              if (!skisSpeedMap.has(skisIdStr)) {
                skisSpeedMap.set(skisIdStr, new Map());
              }
              const skiDateMap = skisSpeedMap.get(skisIdStr)!;
              if (!skiDateMap.has(session.date)) {
                skiDateMap.set(session.date, []);
              }
              skiDateMap.get(session.date)!.push(round.speed!);
            } else {
              dateMap.get(session.date)!.push(round.speed!);
            }
          });
        });
      }
    });

    // Process banana cup jumps (only if banana_cup is selected)
    if (selectedSessionTypes.includes('banana_cup')) {
      bananaCups.forEach(bananaCup => {
        if (!bananaCup.competitions || !Array.isArray(bananaCup.competitions)) return;
        
        bananaCup.competitions.forEach((competition: any) => {
          if (!competition.isCompleted || !competition.jumps || !Array.isArray(competition.jumps)) return;
          
          // Check if competition date is in range
          if (!isDateInRange(competition.date, startDate, endDate)) return;
          if (selectedLocation && competition.location !== selectedLocation) return;
          
          competition.jumps.forEach((jump: any) => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          // Check if athlete is selected (only if skis are not selected)
          if (selectedSkis.length === 0 && !selectedAthletes.includes(athleteId)) return;
          
          const jumpHillSize = jump.hillSize || competition.hillSize;
          if (selectedHillSize && jumpHillSize !== selectedHillSize) return;

          // Initialize dateMap for athlete if not grouping by skis
          let dateMap: Map<string, number[]> | undefined;
          if (!groupBySkis) {
            if (!athleteSpeedMap.has(athleteId)) {
              athleteSpeedMap.set(athleteId, new Map());
            }
            dateMap = athleteSpeedMap.get(athleteId)!;
            if (!dateMap.has(competition.date)) {
              dateMap.set(competition.date, []);
            }
          }

          // Process all rounds (banana cup can have up to 5 rounds)
          const rounds = [
            { speed: jump.firstRoundSpeed, gate: jump.firstRoundStartingGate, skisId: jump.skisId },
            { speed: jump.secondRoundSpeed, gate: jump.secondRoundStartingGate, skisId: jump.skisId },
            { speed: jump.thirdRoundSpeed, gate: jump.thirdRoundStartingGate, skisId: jump.skisId },
            { speed: jump.fourthRoundSpeed, gate: jump.fourthRoundStartingGate, skisId: jump.skisId },
            { speed: jump.fifthRoundSpeed, gate: jump.fifthRoundStartingGate, skisId: jump.skisId },
          ].filter(r => r.speed);

          rounds.forEach(round => {
            // Check gate if selected
            if (selectedGate && round.gate !== Number(selectedGate)) return;
            
            // Check skis if selected
            const skisId = typeof round.skisId === 'object' 
              ? (round.skisId as any)._id 
              : round.skisId;
            const skisIdStr = String(skisId);
            if (selectedSkis.length > 0 && !selectedSkis.includes(skisIdStr)) return;

            if (groupBySkis) {
              // Group by ski
              if (!skisSpeedMap.has(skisIdStr)) {
                skisSpeedMap.set(skisIdStr, new Map());
              }
              const skiDateMap = skisSpeedMap.get(skisIdStr)!;
              if (!skiDateMap.has(competition.date)) {
                skiDateMap.set(competition.date, []);
              }
              skiDateMap.get(competition.date)!.push(round.speed!);
            } else {
              // Group by athlete
              if (dateMap) {
                dateMap.get(competition.date)!.push(round.speed!);
              }
            }
          });
          });
        });
      });
    }

    // Generate complete date range for line charts
    const start = new Date(startDate);
    const end = new Date(endDate);
    const allDates: string[] = [];
    const current = new Date(start);
    while (current <= end) {
      allDates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }

    // Daily data
    const avgLineDataDaily: any[] = [];
    const topLineDataDaily: any[] = [];

    allDates.forEach(date => {
      const avgPoint: any = { date };
      const topPoint: any = { date };

      if (groupBySkis) {
        selectedSkis.forEach(skisIdStr => {
          const skisItem = equipment.find(eq => String(eq._id) === skisIdStr);
          const skisName = skisItem ? skisItem.name : `Skis ${skisIdStr}`;
          const dateMap = skisSpeedMap.get(skisIdStr);
          
          if (dateMap && dateMap.has(date)) {
            const speeds = dateMap.get(date)!;
            const avg = speeds.reduce((a, b) => a + b, 0) / speeds.length;
            const top = Math.max(...speeds);

            avgPoint[skisName] = Math.round(avg * 10) / 10;
            topPoint[skisName] = top;
          } else {
            avgPoint[skisName] = null;
            topPoint[skisName] = null;
          }
        });
      } else {
        athletesToProcess.forEach(athlete => {
          const athleteName = `${athlete.firstName} ${athlete.lastName}`;
          const athleteIdStr = String(athlete._id);
          const dateMap = athleteSpeedMap.get(athleteIdStr);
          
          if (dateMap && dateMap.has(date)) {
            const speeds = dateMap.get(date)!;
            const avg = speeds.reduce((a, b) => a + b, 0) / speeds.length;
            const top = Math.max(...speeds);

            avgPoint[athleteName] = Math.round(avg * 10) / 10;
            topPoint[athleteName] = top;
          } else {
            avgPoint[athleteName] = null;
            topPoint[athleteName] = null;
          }
        });
      }

      avgLineDataDaily.push(avgPoint);
      topLineDataDaily.push(topPoint);
    });

    // Weekly data
    const avgLineDataWeekly: any[] = [];
    const topLineDataWeekly: any[] = [];
    const weekStart = new Date(start);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1); // Start of week (Monday)
    
    while (weekStart <= end) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const weekKey = weekStart.toISOString().split('T')[0];
      const avgPoint: any = { week: weekKey };
      const topPoint: any = { week: weekKey };

      if (groupBySkis) {
        selectedSkis.forEach(skisIdStr => {
          const skisItem = equipment.find(eq => String(eq._id) === skisIdStr);
          const skisName = skisItem ? skisItem.name : `Skis ${skisIdStr}`;
          const dateMap = skisSpeedMap.get(skisIdStr);
          
          const weekSpeeds: number[] = [];
          const checkDate = new Date(weekStart);
          while (checkDate <= weekEnd && checkDate <= end) {
            const dateStr = checkDate.toISOString().split('T')[0];
            if (dateMap && dateMap.has(dateStr)) {
              weekSpeeds.push(...dateMap.get(dateStr)!);
            }
            checkDate.setDate(checkDate.getDate() + 1);
          }
          
          if (weekSpeeds.length > 0) {
            const avg = weekSpeeds.reduce((a, b) => a + b, 0) / weekSpeeds.length;
            const top = Math.max(...weekSpeeds);
            avgPoint[skisName] = Math.round(avg * 10) / 10;
            topPoint[skisName] = top;
          } else {
            avgPoint[skisName] = null;
            topPoint[skisName] = null;
          }
        });
      } else {
        athletesToProcess.forEach(athlete => {
          const athleteName = `${athlete.firstName} ${athlete.lastName}`;
          const athleteIdStr = String(athlete._id);
          const dateMap = athleteSpeedMap.get(athleteIdStr);
          
          const weekSpeeds: number[] = [];
          const checkDate = new Date(weekStart);
          while (checkDate <= weekEnd && checkDate <= end) {
            const dateStr = checkDate.toISOString().split('T')[0];
            if (dateMap && dateMap.has(dateStr)) {
              weekSpeeds.push(...dateMap.get(dateStr)!);
            }
            checkDate.setDate(checkDate.getDate() + 1);
          }
          
          if (weekSpeeds.length > 0) {
            const avg = weekSpeeds.reduce((a, b) => a + b, 0) / weekSpeeds.length;
            const top = Math.max(...weekSpeeds);
            avgPoint[athleteName] = Math.round(avg * 10) / 10;
            topPoint[athleteName] = top;
          } else {
            avgPoint[athleteName] = null;
            topPoint[athleteName] = null;
          }
        });
      }

      avgLineDataWeekly.push(avgPoint);
      topLineDataWeekly.push(topPoint);
      weekStart.setDate(weekStart.getDate() + 7);
    }

    // Monthly data
    const avgLineDataMonthly: any[] = [];
    const topLineDataMonthly: any[] = [];
    const monthStart = new Date(start.getFullYear(), start.getMonth(), 1);
    
    while (monthStart <= end) {
      const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
      const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const avgPoint: any = { month: monthLabel };
      const topPoint: any = { month: monthLabel };

      if (groupBySkis) {
        selectedSkis.forEach(skisIdStr => {
          const skisItem = equipment.find(eq => String(eq._id) === skisIdStr);
          const skisName = skisItem ? skisItem.name : `Skis ${skisIdStr}`;
          const dateMap = skisSpeedMap.get(skisIdStr);
          
          const monthSpeeds: number[] = [];
          const checkDate = new Date(monthStart);
          while (checkDate <= monthEnd && checkDate <= end) {
            const dateStr = checkDate.toISOString().split('T')[0];
            if (dateMap && dateMap.has(dateStr)) {
              monthSpeeds.push(...dateMap.get(dateStr)!);
            }
            checkDate.setDate(checkDate.getDate() + 1);
          }
          
          if (monthSpeeds.length > 0) {
            const avg = monthSpeeds.reduce((a, b) => a + b, 0) / monthSpeeds.length;
            const top = Math.max(...monthSpeeds);
            avgPoint[skisName] = Math.round(avg * 10) / 10;
            topPoint[skisName] = top;
          } else {
            avgPoint[skisName] = null;
            topPoint[skisName] = null;
          }
        });
      } else {
        athletesToProcess.forEach(athlete => {
          const athleteName = `${athlete.firstName} ${athlete.lastName}`;
          const athleteIdStr = String(athlete._id);
          const dateMap = athleteSpeedMap.get(athleteIdStr);
          
          const monthSpeeds: number[] = [];
          const checkDate = new Date(monthStart);
          while (checkDate <= monthEnd && checkDate <= end) {
            const dateStr = checkDate.toISOString().split('T')[0];
            if (dateMap && dateMap.has(dateStr)) {
              monthSpeeds.push(...dateMap.get(dateStr)!);
            }
            checkDate.setDate(checkDate.getDate() + 1);
          }
          
          if (monthSpeeds.length > 0) {
            const avg = monthSpeeds.reduce((a, b) => a + b, 0) / monthSpeeds.length;
            const top = Math.max(...monthSpeeds);
            avgPoint[athleteName] = Math.round(avg * 10) / 10;
            topPoint[athleteName] = top;
          } else {
            avgPoint[athleteName] = null;
            topPoint[athleteName] = null;
          }
        });
      }

      avgLineDataMonthly.push(avgPoint);
      topLineDataMonthly.push(topPoint);
      monthStart.setMonth(monthStart.getMonth() + 1);
    }

    setAvgSpeedLineDataDaily(avgLineDataDaily);
    setAvgSpeedLineDataWeekly(avgLineDataWeekly);
    setAvgSpeedLineDataMonthly(avgLineDataMonthly);
    setTopSpeedLineDataDaily(topLineDataDaily);
    setTopSpeedLineDataWeekly(topLineDataWeekly);
    setTopSpeedLineDataMonthly(topLineDataMonthly);
  }, [sessions, startDate, endDate, selectedAthletes, selectedLocation, selectedHillSize, selectedGate, selectedSkis, athletes, bananaCups, selectedSessionTypes]);

  // Process speed by gate number data
  useEffect(() => {
    // Allow processing if athletes are selected OR skis are selected
    if (!sessions.length || !startDate || !endDate || selectedSessionTypes.length === 0 || (selectedAthletes.length === 0 && selectedSkis.length === 0)) {
      setAvgSpeedByGateData([]);
      return;
    }

    // Helper function to check if a session has been recorded (has actual data)
    const isRecordedSession = (session: TrainingSession): boolean => {
      const hasJumps = !!(session.jumps && session.jumps.length > 0);
      const hasCompetitionJumps = !!(session.competitionJumps && session.competitionJumps.length > 0);
      const hasGroundTrainings = !!(session.groundTrainings && session.groundTrainings.length > 0);
      const hasCrossCountryTrainings = !!(session.crossCountryTrainings && session.crossCountryTrainings.length > 0);
      return hasJumps || hasCompetitionJumps || hasGroundTrainings || hasCrossCountryTrainings;
    };

    // Filter sessions by date range, location, and session types (NOT hillSize at session level)
    const filteredSessions = sessions.filter(session => {
      if (!isDateInRange(session.date, startDate, endDate)) return false;
      if (selectedLocation && session.location !== selectedLocation) return false;
      if (!selectedSessionTypes.includes(session.trainingType)) return false;
      if (!isRecordedSession(session)) return false;
      return true;
    });

    // Determine if we're grouping by skis or athletes
    const groupBySkis = selectedSkis.length > 0;
    
    // Get athletes to process (if skis are selected, we need all athletes to find their skis)
    const athletesToProcess = groupBySkis 
      ? athletes.filter(a => selectedAthletes.length === 0 || selectedAthletes.includes(a._id))
      : athletes.filter(a => selectedAthletes.includes(a._id));

    // For speed by gate: athleteId/skisId -> gate -> speeds[]
    const athleteGateStatsMap = new Map<string, Map<number, { speeds: number[] }>>();
    const skisGateStatsMap = new Map<string, Map<number, { speeds: number[] }>>();

    filteredSessions.forEach(session => {
      // Process regular jumps
      if (session.jumps) {
        session.jumps.forEach(jump => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          // Check athlete filter (if not grouping by skis)
          if (!groupBySkis && !selectedAthletes.includes(athleteId)) return;
          if (groupBySkis && selectedAthletes.length > 0 && !selectedAthletes.includes(athleteId)) return;
          
          // Check skis filter
          const skisId = typeof jump.skisId === 'object' ? (jump.skisId as any)._id : jump.skisId;
          const skisIdStr = String(skisId);
          if (groupBySkis && !selectedSkis.includes(skisIdStr)) return;
          
          const jumpHillSize = jump.hillSize || session.hillSize;
          if (selectedHillSize && jumpHillSize !== selectedHillSize) return;
          if (selectedGate && jump.startingGate !== Number(selectedGate)) return;

          if (jump.startingGate && jump.speed) {
            if (groupBySkis) {
              // Group by skis
              if (!skisGateStatsMap.has(skisIdStr)) {
                skisGateStatsMap.set(skisIdStr, new Map());
              }
              const gateStatsMap = skisGateStatsMap.get(skisIdStr)!;
              if (!gateStatsMap.has(jump.startingGate)) {
                gateStatsMap.set(jump.startingGate, { speeds: [] });
              }
              const stats = gateStatsMap.get(jump.startingGate)!;
              stats.speeds.push(jump.speed);
            } else {
              // Group by athlete
              if (!athleteGateStatsMap.has(athleteId)) {
                athleteGateStatsMap.set(athleteId, new Map());
              }
              const gateStatsMap = athleteGateStatsMap.get(athleteId)!;
              if (!gateStatsMap.has(jump.startingGate)) {
                gateStatsMap.set(jump.startingGate, { speeds: [] });
              }
              const stats = gateStatsMap.get(jump.startingGate)!;
              stats.speeds.push(jump.speed);
            }
          }
        });
      }

      // Process competition jumps
      if (session.competitionJumps) {
        session.competitionJumps.forEach(jump => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          // Check athlete filter (if not grouping by skis)
          if (!groupBySkis && !selectedAthletes.includes(athleteId)) return;
          if (groupBySkis && selectedAthletes.length > 0 && !selectedAthletes.includes(athleteId)) return;
          
          const jumpHillSize = jump.hillSize || session.hillSize;
          if (selectedHillSize && jumpHillSize !== selectedHillSize) return;

          const rounds = [
            { gate: (jump as any).trialRoundStartingGate, speed: (jump as any).trialRoundSpeed, skisId: (jump as any).trialRoundSkisId },
            { gate: jump.firstRoundStartingGate, speed: jump.firstRoundSpeed, skisId: jump.firstRoundSkisId },
            { gate: jump.secondRoundStartingGate, speed: jump.secondRoundSpeed, skisId: jump.secondRoundSkisId },
            { gate: (jump as any).thirdRoundStartingGate, speed: (jump as any).thirdRoundSpeed, skisId: (jump as any).thirdRoundSkisId },
            { gate: (jump as any).fourthRoundStartingGate, speed: (jump as any).fourthRoundSpeed, skisId: (jump as any).fourthRoundSkisId },
            { gate: (jump as any).fifthRoundStartingGate, speed: (jump as any).fifthRoundSpeed, skisId: (jump as any).fifthRoundSkisId },
          ].filter(r => r.gate !== undefined && r.gate !== null);

          rounds.forEach(round => {
            if (selectedGate && round.gate !== Number(selectedGate)) return;
            
            // Check skis filter for this round
            if (groupBySkis) {
              const roundSkisId = typeof round.skisId === 'object' ? (round.skisId as any)._id : round.skisId;
              const roundSkisIdStr = String(roundSkisId);
              if (!selectedSkis.includes(roundSkisIdStr)) return;
            }

            if (round.speed) {
              if (groupBySkis) {
                // Group by skis
                const roundSkisId = typeof round.skisId === 'object' ? (round.skisId as any)._id : round.skisId;
                const roundSkisIdStr = String(roundSkisId);
                if (!skisGateStatsMap.has(roundSkisIdStr)) {
                  skisGateStatsMap.set(roundSkisIdStr, new Map());
                }
                const gateStatsMap = skisGateStatsMap.get(roundSkisIdStr)!;
                if (!gateStatsMap.has(round.gate!)) {
                  gateStatsMap.set(round.gate!, { speeds: [] });
                }
                const stats = gateStatsMap.get(round.gate!)!;
                stats.speeds.push(round.speed);
              } else {
                // Group by athlete
                if (!athleteGateStatsMap.has(athleteId)) {
                  athleteGateStatsMap.set(athleteId, new Map());
                }
                const gateStatsMap = athleteGateStatsMap.get(athleteId)!;
                if (!gateStatsMap.has(round.gate!)) {
                  gateStatsMap.set(round.gate!, { speeds: [] });
                }
                const stats = gateStatsMap.get(round.gate!)!;
                stats.speeds.push(round.speed);
              }
            }
          });
        });
      }
    });

    // Process banana cup jumps
    if (selectedSessionTypes.includes('banana_cup')) {
      bananaCups.forEach((bananaCup: any) => {
        if (!bananaCup.competitions || !Array.isArray(bananaCup.competitions)) return;
        
        bananaCup.competitions.forEach((competition: any) => {
          if (!competition.isCompleted || !competition.jumps || !Array.isArray(competition.jumps)) return;
          if (!isDateInRange(competition.date, startDate, endDate)) return;
          if (selectedLocation && competition.location !== selectedLocation) return;
          
          competition.jumps.forEach((jump: any) => {
            const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
            const athleteId = String(athleteIdRaw);
            
            // Check athlete filter (if not grouping by skis)
            if (!groupBySkis && !selectedAthletes.includes(athleteId)) return;
            if (groupBySkis && selectedAthletes.length > 0 && !selectedAthletes.includes(athleteId)) return;
            
            const jumpHillSize = jump.hillSize || competition.hillSize;
            if (selectedHillSize && jumpHillSize !== selectedHillSize) return;

            const rounds = [
              { gate: jump.firstRoundStartingGate, speed: jump.firstRoundSpeed, skisId: jump.firstRoundSkisId },
              { gate: jump.secondRoundStartingGate, speed: jump.secondRoundSpeed, skisId: jump.secondRoundSkisId },
              { gate: jump.thirdRoundStartingGate, speed: jump.thirdRoundSpeed, skisId: jump.thirdRoundSkisId },
              { gate: jump.fourthRoundStartingGate, speed: jump.fourthRoundSpeed, skisId: jump.fourthRoundSkisId },
              { gate: jump.fifthRoundStartingGate, speed: jump.fifthRoundSpeed, skisId: jump.fifthRoundSkisId },
            ].filter(r => r.gate !== undefined && r.gate !== null);

            rounds.forEach(round => {
              if (selectedGate && round.gate !== Number(selectedGate)) return;
              
              // Check skis filter for this round
              if (groupBySkis) {
                const roundSkisId = typeof round.skisId === 'object' ? (round.skisId as any)._id : round.skisId;
                const roundSkisIdStr = String(roundSkisId);
                if (!selectedSkis.includes(roundSkisIdStr)) return;
              }

              if (round.speed) {
                if (groupBySkis) {
                  // Group by skis
                  const roundSkisId = typeof round.skisId === 'object' ? (round.skisId as any)._id : round.skisId;
                  const roundSkisIdStr = String(roundSkisId);
                  if (!skisGateStatsMap.has(roundSkisIdStr)) {
                    skisGateStatsMap.set(roundSkisIdStr, new Map());
                  }
                  const gateStatsMap = skisGateStatsMap.get(roundSkisIdStr)!;
                  if (!gateStatsMap.has(round.gate!)) {
                    gateStatsMap.set(round.gate!, { speeds: [] });
                  }
                  const stats = gateStatsMap.get(round.gate!)!;
                  stats.speeds.push(round.speed);
                } else {
                  // Group by athlete
                  if (!athleteGateStatsMap.has(athleteId)) {
                    athleteGateStatsMap.set(athleteId, new Map());
                  }
                  const gateStatsMap = athleteGateStatsMap.get(athleteId)!;
                  if (!gateStatsMap.has(round.gate!)) {
                    gateStatsMap.set(round.gate!, { speeds: [] });
                  }
                  const stats = gateStatsMap.get(round.gate!)!;
                  stats.speeds.push(round.speed);
                }
              }
            });
          });
        });
      });
    }

    // Process data for speed by gate number
    // Get all unique gates used
    const allGates = new Set<number>();
    
    if (groupBySkis) {
      skisGateStatsMap.forEach((gateStatsMap) => {
        gateStatsMap.forEach((_, gate) => allGates.add(gate));
      });
    } else {
      athletesToProcess.forEach(athlete => {
        const athleteIdStr = String(athlete._id);
        const gateStatsMap = athleteGateStatsMap.get(athleteIdStr);
        if (gateStatsMap) {
          gateStatsMap.forEach((_, gate) => allGates.add(gate));
        }
      });
    }
    
    const sortedGates = Array.from(allGates).sort((a, b) => a - b);

    const speedByGateData: any[] = [];

    sortedGates.forEach(gate => {
      const speedPoint: any = { gate: `Gate ${gate}` };
      
      if (groupBySkis) {
        // Group by skis
        selectedSkis.forEach(skisIdStr => {
          const skisItem = equipment.find(eq => String(eq._id) === skisIdStr);
          const skisName = skisItem ? skisItem.name : `Skis ${skisIdStr}`;
          const gateStatsMap = skisGateStatsMap.get(skisIdStr);
          
          if (gateStatsMap && gateStatsMap.has(gate)) {
            const stats = gateStatsMap.get(gate)!;
            if (stats.speeds.length > 0) {
              const avgSpeed = stats.speeds.reduce((a, b) => a + b, 0) / stats.speeds.length;
              speedPoint[skisName] = Math.round(avgSpeed * 10) / 10;
            } else {
              speedPoint[skisName] = null;
            }
          } else {
            speedPoint[skisName] = null;
          }
        });
      } else {
        // Group by athletes
        athletesToProcess.forEach(athlete => {
          const athleteName = `${athlete.firstName} ${athlete.lastName}`;
          const athleteIdStr = String(athlete._id);
          const gateStatsMap = athleteGateStatsMap.get(athleteIdStr);
          
          if (gateStatsMap && gateStatsMap.has(gate)) {
            const stats = gateStatsMap.get(gate)!;
            if (stats.speeds.length > 0) {
              const avgSpeed = stats.speeds.reduce((a, b) => a + b, 0) / stats.speeds.length;
              speedPoint[athleteName] = Math.round(avgSpeed * 10) / 10;
            } else {
              speedPoint[athleteName] = null;
            }
          } else {
            speedPoint[athleteName] = null;
          }
        });
      }
      
      speedByGateData.push(speedPoint);
    });

    setAvgSpeedByGateData(speedByGateData);
  }, [sessions, startDate, endDate, selectedAthletes, selectedSkis, selectedLocation, selectedHillSize, selectedGate, athletes, equipment, bananaCups, selectedSessionTypes]);

  // Helper function to calculate Y-axis domain with padding (1 value lower/higher)
  const calculateYAxisDomainWithPadding = (data: any[], dataKeys: string[]): [number, number] => {
    let minValue = Infinity;
    let maxValue = -Infinity;
    
    data.forEach(point => {
      dataKeys.forEach(key => {
        const value = point[key];
        if (value !== null && value !== undefined && typeof value === 'number') {
          if (value < minValue) {
            minValue = value;
          }
          if (value > maxValue) {
            maxValue = value;
          }
        }
      });
    });
    
    if (minValue === Infinity || maxValue === -Infinity) {
      return [0, 100];
    }
    
    // Round to 1 decimal place for padding
    const padding = 1.0;
    return [Math.round((minValue - padding) * 10) / 10, Math.round((maxValue + padding) * 10) / 10];
  };

  const handleAthleteToggle = (athleteId: string) => {
    setSelectedAthletes(prev =>
      prev.includes(athleteId)
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const handleSkisToggle = (skisId: string) => {
    setSelectedSkis(prev =>
      prev.includes(skisId)
        ? prev.filter(id => id !== skisId)
        : [...prev, skisId]
    );
  };

  const handleUpdateDate = () => {
    // Data will be re-processed automatically via useEffect
  };

  const handleQuickDateRange = (range: string) => {
    const { startDate: newStartDate, endDate: newEndDate } = getQuickDateRange(range);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleSessionTypeToggle = (sessionType: string) => {
    setSelectedSessionTypes(prev =>
      prev.includes(sessionType)
        ? prev.filter(type => type !== sessionType)
        : [...prev, sessionType]
    );
  };

  const handleAthletesSave = (selected: string[]) => {
    setSelectedAthletes(selected);
  };

  const handleSkisSave = (selected: string[]) => {
    setSelectedSkis(selected);
  };

  const handleSessionTypesSave = (types: string[]) => {
    setSelectedSessionTypes(types);
  };

  // Default colors for equipment (equipment doesn't have assigned colors like athletes)
  const getEquipmentColors = () => {
    return [
      '#ff9600', '#0066cc', '#00cc66', '#cc0066', '#6600cc',
      '#cc6600', '#00cccc', '#cc0066', '#0066ff', '#ff6600',
    ];
  };

  // Helper function to calculate Y-axis domain (min - 10, auto max)
  const calculateYAxisDomain = (data: any[], dataKeys: string[]): [number, string] => {
    let minValue = Infinity;
    
    data.forEach(point => {
      dataKeys.forEach(key => {
        const value = point[key];
        if (value !== null && value !== undefined && typeof value === 'number' && value < minValue) {
          minValue = value;
        }
      });
    });
    
    if (minValue === Infinity) {
      return [0, 'auto'];
    }
    
    return [Math.max(0, minValue - 10), 'auto'];
  };

  if (isLoading) {
    return (
      <AnalyticsContainer>
        <EmptyState>Loading speed data...</EmptyState>
      </AnalyticsContainer>
    );
  }

  return (
    <AnalyticsContainer>
      <PageHeader>
        <PageTitle>Inrun Speed Analytics</PageTitle>
        <PageDescription>
          Analyze speed metrics from training and competition sessions to identify performance patterns.
        </PageDescription>
      </PageHeader>

      {/* Data Selection Section */}
      <DataSelectionSection>
        <SelectionGrid>
          <SelectionGroup>
            <DateRangeDropdown
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onQuickDateRange={handleQuickDateRange}
              onUpdate={handleUpdateDate}
              formatDateForInput={formatDateForInput}
            />
          </SelectionGroup>

          {!isJumperMode && (
            <SelectionGroup>
              <AthletesDropdown
                athletes={athletes}
                selectedAthletes={selectedAthletes}
                onAthleteToggle={handleAthleteToggle}
                onSave={handleAthletesSave}
              />
            </SelectionGroup>
          )}

          <SelectionGroup>
            <SessionTypesDropdown
              selectedSessionTypes={selectedSessionTypes}
              onSessionTypeToggle={handleSessionTypeToggle}
              onSave={handleSessionTypesSave}
            />
          </SelectionGroup>

          <SelectionGroup>
            <LocationDropdown
              locations={locations}
              selectedLocation={selectedLocation}
              onLocationChange={setSelectedLocation}
            />
          </SelectionGroup>

          <SelectionGroup>
            <HillSizeDropdown
              hillSizes={hillSizes}
              selectedHillSize={selectedHillSize}
              onHillSizeChange={setSelectedHillSize}
            />
          </SelectionGroup>

          <SelectionGroup>
            <GateDropdown
              gates={gates}
              selectedGate={selectedGate}
              onGateChange={setSelectedGate}
            />
          </SelectionGroup>

          {selectedAthletes.length > 0 && (
            <SelectionGroup>
              <EquipmentDropdown
                equipment={filteredSkis}
                selectedEquipment={selectedSkis}
                onEquipmentToggle={handleSkisToggle}
                onSave={handleSkisSave}
                athletes={athletes}
                label="Skis"
              />
            </SelectionGroup>
          )}
        </SelectionGrid>
      </DataSelectionSection>

      {/* Graphs Section */}
      
      {/* Average Speed Line Chart - Full Width */}
      <GraphSectionWithPeriodSelector>
        <GraphTitle>{isJumperMode ? 'Average Speed' : 'Average Speed of Each Athlete'}</GraphTitle>
        {startDate && endDate && selectedSessionTypes.length > 0 && (selectedAthletes.length > 0 || selectedSkis.length > 0) ? (
          <>
            {(selectedPeriod === 'daily' && avgSpeedLineDataDaily.length > 0) ||
             (selectedPeriod === 'weekly' && avgSpeedLineDataWeekly.length > 0) ||
             (selectedPeriod === 'monthly' && avgSpeedLineDataMonthly.length > 0) ? (
              <>
                <PeriodSelectorTopRight>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'daily'}
                      onClick={() => setSelectedPeriod('daily')}
                    >
                      D
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Daily</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'weekly'}
                      onClick={() => setSelectedPeriod('weekly')}
                    >
                      W
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Weekly</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'monthly'}
                      onClick={() => setSelectedPeriod('monthly')}
                    >
                      M
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Monthly</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                </PeriodSelectorTopRight>
                <GraphContainer>
                  <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                    <LineChart data={
                      selectedPeriod === 'daily' ? avgSpeedLineDataDaily :
                      selectedPeriod === 'weekly' ? avgSpeedLineDataWeekly :
                      avgSpeedLineDataMonthly
                    }>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey={selectedPeriod === 'daily' ? 'date' : selectedPeriod === 'weekly' ? 'week' : 'month'} 
                        style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                        tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                        tickFormatter={(value) => {
                          if (selectedPeriod === 'daily') {
                            try {
                              const date = new Date(value);
                              if (isNaN(date.getTime())) return value;
                              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            } catch {
                              return value;
                            }
                          } else if (selectedPeriod === 'weekly') {
                            try {
                              const date = new Date(value);
                              if (isNaN(date.getTime())) return value;
                              const weekEnd = new Date(date);
                              weekEnd.setDate(weekEnd.getDate() + 6);
                              return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                            } catch {
                              return value;
                            }
                          } else {
                            return value;
                          }
                        }}
                      />
                      <YAxis 
                        domain={calculateYAxisDomain(
                          selectedPeriod === 'daily' ? avgSpeedLineDataDaily :
                          selectedPeriod === 'weekly' ? avgSpeedLineDataWeekly :
                          avgSpeedLineDataMonthly,
                          selectedSkis.length > 0
                            ? selectedSkis.map(skisId => {
                                const skisItem = equipment.find(eq => String(eq._id) === skisId);
                                return skisItem ? skisItem.name : `Skis ${skisId}`;
                              })
                            : athletes
                                .filter(a => selectedAthletes.length > 0 ? selectedAthletes.includes(a._id) : true)
                                .map(a => `${a.firstName} ${a.lastName}`)
                        )} 
                        style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                        tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      />
                <Tooltip />
                <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                {selectedSkis.length > 0
                  ? selectedSkis.map((skisId, index) => {
                      const colors = getEquipmentColors();
                      const skisItem = equipment.find(eq => String(eq._id) === skisId);
                      const skisName = skisItem ? skisItem.name : `Skis ${skisId}`;
                      return (
                        <Line
                          key={skisId}
                          type="monotone"
                          dataKey={skisName}
                          stroke={colors[index % colors.length]}
                          strokeWidth={2}
                          name={skisName}
                          connectNulls={true}
                        />
                      );
                    })
                  : athletes
                      .filter(a => selectedAthletes.length === 0 || selectedAthletes.includes(a._id))
                      .map((athlete, index) => {
                        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                        return (
                          <Line
                            key={athlete._id}
                            type="monotone"
                            dataKey={athleteName}
                            stroke={getAthleteColor(athlete, index)}
                            strokeWidth={2}
                            name={athleteName}
                            connectNulls={true}
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                </GraphContainer>
              </>
            ) : (
              <EmptyState>No average speed data available</EmptyState>
            )}
          </>
        ) : (
          <EmptyState>
            {!startDate || !endDate
              ? 'Please select a date range to view average speed data.'
              : selectedSessionTypes.length === 0
              ? 'Please select at least one session type to view average speed data.'
              : (selectedAthletes.length === 0 && selectedSkis.length === 0)
              ? 'Please select at least one athlete or ski to view average speed data.'
              : 'No average speed data available for the selected filters.'}
          </EmptyState>
        )}
      </GraphSectionWithPeriodSelector>

      {/* Top Speed Line Chart - Full Width */}
      <GraphSectionWithPeriodSelector>
        <GraphTitle>{isJumperMode ? 'Top Speed' : 'Top Speed of Each Athlete'}</GraphTitle>
        {startDate && endDate && selectedSessionTypes.length > 0 && (selectedAthletes.length > 0 || selectedSkis.length > 0) ? (
          <>
            {(selectedPeriod === 'daily' && topSpeedLineDataDaily.length > 0) ||
             (selectedPeriod === 'weekly' && topSpeedLineDataWeekly.length > 0) ||
             (selectedPeriod === 'monthly' && topSpeedLineDataMonthly.length > 0) ? (
              <>
                <PeriodSelectorTopRight>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'daily'}
                      onClick={() => setSelectedPeriod('daily')}
                    >
                      D
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Daily</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'weekly'}
                      onClick={() => setSelectedPeriod('weekly')}
                    >
                      W
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Weekly</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'monthly'}
                      onClick={() => setSelectedPeriod('monthly')}
                    >
                      M
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Monthly</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                </PeriodSelectorTopRight>
                <GraphContainer>
                  <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                    <LineChart data={
                      selectedPeriod === 'daily' ? topSpeedLineDataDaily :
                      selectedPeriod === 'weekly' ? topSpeedLineDataWeekly :
                      topSpeedLineDataMonthly
                    }>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey={selectedPeriod === 'daily' ? 'date' : selectedPeriod === 'weekly' ? 'week' : 'month'} 
                        style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                        tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                        tickFormatter={(value) => {
                          if (selectedPeriod === 'daily') {
                            try {
                              const date = new Date(value);
                              if (isNaN(date.getTime())) return value;
                              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            } catch {
                              return value;
                            }
                          } else if (selectedPeriod === 'weekly') {
                            try {
                              const date = new Date(value);
                              if (isNaN(date.getTime())) return value;
                              const weekEnd = new Date(date);
                              weekEnd.setDate(weekEnd.getDate() + 6);
                              return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                            } catch {
                              return value;
                            }
                          } else {
                            return value;
                          }
                        }}
                      />
                      <YAxis 
                        domain={calculateYAxisDomain(
                          selectedPeriod === 'daily' ? topSpeedLineDataDaily :
                          selectedPeriod === 'weekly' ? topSpeedLineDataWeekly :
                          topSpeedLineDataMonthly,
                          selectedSkis.length > 0
                            ? selectedSkis.map(skisId => {
                                const skisItem = equipment.find(eq => String(eq._id) === skisId);
                                return skisItem ? skisItem.name : `Skis ${skisId}`;
                              })
                            : athletes
                                .filter(a => selectedAthletes.length > 0 ? selectedAthletes.includes(a._id) : true)
                                .map(a => `${a.firstName} ${a.lastName}`)
                        )} 
                        style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                        tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                      />
                <Tooltip />
                <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                {selectedSkis.length > 0
                  ? selectedSkis.map((skisId, index) => {
                      const colors = getEquipmentColors();
                      const skisItem = equipment.find(eq => String(eq._id) === skisId);
                      const skisName = skisItem ? skisItem.name : `Skis ${skisId}`;
                      return (
                        <Line
                          key={skisId}
                          type="monotone"
                          dataKey={skisName}
                          stroke={colors[index % colors.length]}
                          strokeWidth={2}
                          name={skisName}
                          connectNulls={true}
                        />
                      );
                    })
                  : athletes
                      .filter(a => selectedAthletes.length === 0 || selectedAthletes.includes(a._id))
                      .map((athlete, index) => {
                        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                        return (
                          <Line
                            key={athlete._id}
                            type="monotone"
                            dataKey={athleteName}
                            stroke={getAthleteColor(athlete, index)}
                            strokeWidth={2}
                            name={athleteName}
                            connectNulls={true}
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                </GraphContainer>
              </>
            ) : (
              <EmptyState>No top speed data available</EmptyState>
            )}
          </>
        ) : (
          <EmptyState>
            {!startDate || !endDate
              ? 'Please select a date range to view top speed data.'
              : selectedSessionTypes.length === 0
              ? 'Please select at least one session type to view top speed data.'
              : (selectedAthletes.length === 0 && selectedSkis.length === 0)
              ? 'Please select at least one athlete or ski to view top speed data.'
              : 'No top speed data available for the selected filters.'}
          </EmptyState>
        )}
      </GraphSectionWithPeriodSelector>

      {/* Average Speed by Gate Number */}
      <GraphSection>
        <GraphTitle>
          {selectedSkis.length > 0 
            ? 'Average Inrun Speed from Each Gate Number'
            : isJumperMode 
            ? 'Average Inrun Speed from Each Gate Number'
            : 'Average Inrun Speed from Each Gate Number for Each Athlete'}
        </GraphTitle>
        {startDate && endDate && selectedSessionTypes.length > 0 && (selectedAthletes.length > 0 || selectedSkis.length > 0) && avgSpeedByGateData.length > 0 ? (
          <GraphContainer>
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <LineChart data={avgSpeedByGateData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="gate" 
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <YAxis 
                  domain={calculateYAxisDomainWithPadding(
                    avgSpeedByGateData,
                    selectedSkis.length > 0
                      ? selectedSkis.map(skisIdStr => {
                          const skisItem = equipment.find(eq => String(eq._id) === skisIdStr);
                          return skisItem ? skisItem.name : `Skis ${skisIdStr}`;
                        })
                      : athletes
                          .filter(a => selectedAthletes.includes(a._id))
                          .map(a => `${a.firstName} ${a.lastName}`)
                  )}
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                  label={{ value: 'Speed (km/h)', angle: -90, position: 'insideLeft', style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                {selectedSkis.length > 0 ? (
                  // Render lines for selected skis
                  selectedSkis.map((skisIdStr, index) => {
                    const skisItem = equipment.find(eq => String(eq._id) === skisIdStr);
                    const skisName = skisItem ? skisItem.name : `Skis ${skisIdStr}`;
                    return (
                      <Line
                        key={skisIdStr}
                        type="monotone"
                        dataKey={skisName}
                        stroke={getAthleteColor({ _id: skisIdStr } as any, index)}
                        strokeWidth={2}
                        name={skisName}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        connectNulls={true}
                      />
                    );
                  })
                ) : (
                  // Render lines for selected athletes
                  athletes
                    .filter(a => selectedAthletes.includes(a._id))
                    .map((athlete, index) => {
                      const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                      return (
                        <Line
                          key={athlete._id}
                          type="monotone"
                          dataKey={athleteName}
                          stroke={getAthleteColor(athlete, index)}
                          strokeWidth={2}
                          name={athleteName}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                          connectNulls={true}
                        />
                      );
                    })
                )}
              </LineChart>
            </ResponsiveContainer>
          </GraphContainer>
        ) : (
          <EmptyState>
            {!startDate || !endDate 
              ? 'Please select a date range'
              : selectedSessionTypes.length === 0
              ? 'Please select at least one session type'
              : (selectedAthletes.length === 0 && selectedSkis.length === 0)
              ? 'Please select at least one athlete or ski'
              : 'No data available for the selected filters'}
          </EmptyState>
        )}
      </GraphSection>
    </AnalyticsContainer>
  );
};

export default SpeedAnalytics;

